import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { graphqlCall } from "../helper/Common";
import { MARKETS_CURRENCIES_QUERY, SHOP_CURRENCIES_FALLBACK_QUERY } from "../graphql/marketQueries";
import {
  optionsFromInstallCurrency,
  optionsFromMarkets,
  optionsFromShop,
  resolvePrimaryCurrency,
} from "../utils/marketCurrencies";

function marketsQueryFailed(res) {
  const msg = res?.errors?.[0]?.message || "";
  return /access denied|unauthorized|scope|read_markets|permission/i.test(msg);
}

/** In-memory cache — survives tab switches; cleared only on full page reload. */
let cachedCurrencies = null;
let fetchPromise = null;

async function loadStoreMarketCurrencies(installCurrency) {
  if (cachedCurrencies) return cachedCurrencies;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const marketsRes = await graphqlCall(MARKETS_CURRENCIES_QUERY);
      let options = [];
      let primary = installCurrency || "USD";

      if (!marketsQueryFailed(marketsRes)) {
        options = optionsFromMarkets(marketsRes?.data);
        primary =
          marketsRes?.data?.shop?.currencyCode ||
          resolvePrimaryCurrency(options, installCurrency || "USD");
      }

      if (!options.length) {
        const shopRes = await graphqlCall(SHOP_CURRENCIES_FALLBACK_QUERY);
        options = optionsFromShop(shopRes?.data);
        primary = shopRes?.data?.shop?.currencyCode || primary;
      }

      if (!options.length) {
        options = optionsFromInstallCurrency(installCurrency);
        primary = installCurrency || "USD";
      }

      const result = {
        currencyOptions: options,
        primaryCurrency: resolvePrimaryCurrency(options, primary),
      };
      cachedCurrencies = result;
      return result;
    } catch {
      const result = {
        currencyOptions: optionsFromInstallCurrency(installCurrency),
        primaryCurrency: installCurrency || "USD",
      };
      cachedCurrencies = result;
      return result;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/**
 * Fetches store market currencies once per page load (cached module-wide).
 * Call from the editor page root — not inside tab panels that unmount on switch.
 */
export function useStoreMarketCurrencies() {
  const installCurrency = useSelector((s) => s.install?.installData?.shop_info?.currency);
  const [currencyOptions, setCurrencyOptions] = useState(() => cachedCurrencies?.currencyOptions ?? []);
  const [primaryCurrency, setPrimaryCurrency] = useState(
    () => cachedCurrencies?.primaryCurrency ?? installCurrency ?? "USD",
  );
  const [loading, setLoading] = useState(() => !cachedCurrencies);

  useEffect(() => {
    if (cachedCurrencies) {
      setCurrencyOptions(cachedCurrencies.currencyOptions);
      setPrimaryCurrency(cachedCurrencies.primaryCurrency);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    loadStoreMarketCurrencies(installCurrency).then((result) => {
      if (cancelled) return;
      setCurrencyOptions(result.currencyOptions);
      setPrimaryCurrency(result.primaryCurrency);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [installCurrency]);

  return { currencyOptions, loading, primaryCurrency };
}
